import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { api, tokenManager } from '../lib/api';

// Types
export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  status: 'active' | 'inactive' | 'suspended';
  role: 'superuser' | 'owner' | 'admin' | 'user';
  is_verified: boolean;
  created_at: string;
  updated_at: string;
}

export interface Tenant {
  id: string;
  name: string;
  domain?: string;
  status: 'active' | 'suspended' | 'cancelled';
  created_at: string;
  updated_at: string;
}

export interface AuthState {
  user: User | null;
  tenant: Tenant | null;
  tenants: Tenant[]; // All tenants user belongs to
  isAuthenticated: boolean;
  isLoading: boolean;
  needsAttestation: boolean;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterCredentials {
  email: string;
  first_name: string;
  last_name: string;
  password: string;
  company_name?: string;
  vertical_pack?: string;
  phone_number: string;
  sms_consent?: boolean;
}

export interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (credentials: RegisterCredentials) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  updateProfile: (data: Partial<User>) => Promise<void>;
  completeAttestation: () => Promise<void>;
  switchTenant: (tenantId: string) => Promise<void>;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth provider component
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    tenant: null,
    tenants: [],
    isAuthenticated: false,
    isLoading: true,
    needsAttestation: false,
  });

  // Initialize auth state on mount
  useEffect(() => {
    const initializeAuth = async () => {
      const accessToken = tokenManager.getAccessToken();
      
      if (accessToken) {
        try {
          await refreshUser();
        } catch (error: any) {
          // Clear tokens on any auth error (401, 500, etc.)
          tokenManager.clearTokens();
          tokenManager.clearTenantId();
          
          // Only log unexpected errors (not 401 or 500 which are auth-related)
          if (error?.response?.status !== 401 && error?.response?.status !== 500) {
            console.error('Failed to initialize auth:', error);
          }
        }
      }
      
      setState(prev => ({ ...prev, isLoading: false }));
    };

    initializeAuth();
  }, []);

  const login = async (credentials: LoginCredentials) => {
    try {
      setState(prev => ({ ...prev, isLoading: true }));
      
      const response = await api.auth.login(credentials.email, credentials.password);
      const { access_token, refresh_token } = response.data;
      
      // Store tokens
      tokenManager.setTokens(access_token, refresh_token);
      
      // Fetch user profile and tenant info
      await refreshUser();
      
    } catch (error) {
      setState(prev => ({ ...prev, isLoading: false }));
      throw error;
    }
  };

  const register = async (credentials: RegisterCredentials) => {
    try {
      setState(prev => ({ ...prev, isLoading: true }));
      
      const response = await api.auth.register(credentials);
      
      // Registration successful, but user needs to verify email
      setState(prev => ({ ...prev, isLoading: false }));
      
      return response.data;
      
    } catch (error) {
      setState(prev => ({ ...prev, isLoading: false }));
      throw error;
    }
  };

  const logout = async () => {
    try {
      await api.auth.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear tokens and state regardless of API call success
      tokenManager.clearTokens();
      tokenManager.clearTenantId();
      setState({
        user: null,
        tenant: null,
        tenants: [],
        isAuthenticated: false,
        isLoading: false,
        needsAttestation: false,
      });
    }
  };

  const refreshUser = async () => {
    try {
      const [userResponse, tenantsResponse, tenantResponse, attestationResponse] = await Promise.all([
        api.user.getProfile(),
        api.tenant.getAll().catch(() => ({ data: [] })),
        api.tenant.getCurrent().catch(() => ({ data: null })),
        api.compliance.getAttestationStatus().catch(() => ({ data: { has_attested: false } })),
      ]);
      
      const user = userResponse.data;
      const tenants = tenantsResponse.data || [];
      const tenant = tenantResponse.data;
      const needsAttestation = !attestationResponse.data.has_attested;
      
      // Store tenant ID if not already stored, or if current tenant is not in the list
      const storedTenantId = tokenManager.getTenantId();
      if (tenant?.id) {
        if (!storedTenantId || !tenants.find(t => t.id === tenant.id)) {
          tokenManager.setTenantId(tenant.id);
        }
      } else if (tenants.length > 0 && !storedTenantId) {
        // If no current tenant but user has tenants, use the first one
        tokenManager.setTenantId(tenants[0].id);
      }
      
      setState({
        user,
        tenant: tenant || (tenants.length > 0 ? tenants[0] : null),
        tenants,
        isAuthenticated: true,
        isLoading: false,
        needsAttestation,
      });
    } catch (error: any) {
      // Only log unexpected errors (not 401 or 500 which are auth-related)
      if (error?.response?.status !== 401 && error?.response?.status !== 500) {
        console.error('Failed to refresh user:', error);
      }
      setState({
        user: null,
        tenant: null,
        tenants: [],
        isAuthenticated: false,
        isLoading: false,
        needsAttestation: false,
      });
      throw error;
    }
  };

  const switchTenant = async (tenantId: string) => {
    try {
      // Update stored tenant ID
      tokenManager.setTenantId(tenantId);
      
      // Fetch the new tenant details
      const tenantResponse = await api.tenant.getCurrent();
      const newTenant = tenantResponse.data;
      
      // Update state with new tenant
      setState(prev => ({
        ...prev,
        tenant: newTenant,
      }));
      
      // Refresh user data to get updated tenant context
      await refreshUser();
    } catch (error: any) {
      console.error('Failed to switch tenant:', error);
      // If switching fails, try to refresh to get back to a valid state
      await refreshUser();
      throw error;
    }
  };

  const completeAttestation = async () => {
    // Update state to indicate attestation is complete
    setState(prev => ({
      ...prev,
      needsAttestation: false,
    }));
  };

  const updateProfile = async (data: Partial<User>) => {
    try {
      const response = await api.user.updateProfile(data);
      const updatedUser = response.data;
      
      setState(prev => ({
        ...prev,
        user: updatedUser,
      }));
    } catch (error) {
      console.error('Failed to update profile:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    ...state,
    login,
    register,
    logout,
    refreshUser,
    updateProfile,
    completeAttestation,
    switchTenant,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// HOC for protected routes
export const withAuth = <P extends object>(
  Component: React.ComponentType<P>
): React.FC<P> => {
  return (props: P) => {
    const { isAuthenticated, isLoading } = useAuth();
    
    if (isLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      );
    }
    
    if (!isAuthenticated) {
      // Redirect to login (handled by router)
      return null;
    }
    
    return <Component {...props} />;
  };
};
